import { createSelector } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { intersection, sortBy, uniq } from 'lodash';

import { ORDER_KINDS } from 'config/constants';
import { DeviceSelectors } from 'store/Device';
import UserSelectors from 'store/User/selectors';
import { getLabelKeyForOrderKind } from 'util/helpers';

import { initialSettingsState } from './reducer';
import type { SettingState } from 'store/Settings/types';
import type { RootState } from 'store/types';

const selectSlice = (state: RootState) => state.categories as SettingState;

const selectAllCategories = createSelector(selectSlice, (slice) =>
  sortBy(slice.allCategories, (category) => category && category.toLowerCase()),
);

const selectUserSelectedCategories = createSelector(selectSlice, (slice) =>
  intersection(slice.allCategories, slice.selectedCategories),
);

// BE doesn't guarantee that screens default categories actually intersect with all categories, so we better sanitize them
const selectScreenCategories = createSelector(
  [selectAllCategories, DeviceSelectors.selectScreenCategories],
  (allCategories, deviceCategories) => intersection(allCategories, deviceCategories),
);

const selectSelectedCategories = createSelector(
  [selectUserSelectedCategories, selectScreenCategories],
  (userSelected, screenSelected) => uniq([...userSelected, ...screenSelected]),
);

const selectIsSelectedAllCategories = createSelector(
  selectSlice,
  (slice) => slice.isSelectedAllCategories,
);

const selectSelectedCategoriesCount = createSelector(
  [selectSlice, selectSelectedCategories],
  (slice, selectedCategories) =>
    slice.isSelectedAllCategories ? slice.allCategories.length : selectedCategories.length,
);

// does it need to be a selector?
// probably not, but iirc I want it to be consistent with how we select all categories 😅
const selectAllOrderKinds = createSelector([UserSelectors.selectFeatureFlags], (featureFlags) =>
  sortBy(
    featureFlags.kds_displayed_order_kinds
      ? ORDER_KINDS.filter((orderKind) => featureFlags.kds_displayed_order_kinds[orderKind])
      : ORDER_KINDS,
    (orderKind) => i18n.t(getLabelKeyForOrderKind(orderKind), { lng: 'en' }).toLowerCase(),
  ),
);

const selectUserSelectedOrderKinds = createSelector(
  [selectSlice, selectAllOrderKinds],
  (slice, orderKinds) => intersection(orderKinds, slice.selectedOrderKinds),
);

const selectSelectedOrderKinds = createSelector(
  [selectUserSelectedOrderKinds, DeviceSelectors.selectScreenOrderKinds],
  (userSelected, screenSelected) => uniq([...userSelected, ...screenSelected]),
);

const selectIsSelectedAllOrderKinds = createSelector(
  selectSlice,
  (slice) => slice.isSelectedAllOrderKinds,
);

const selectSelectedOrderKindsCount = createSelector(
  [selectSlice, selectAllOrderKinds, selectSelectedOrderKinds],
  (slice, orderKinds, selectedOrderKinds) =>
    slice.isSelectedAllOrderKinds ? orderKinds.length : selectedOrderKinds.length,
);

const selectItemCookDelayEnabled = createSelector(
  selectSlice,
  (slice) => slice.isItemCookDelayEnabled,
);

const selectItemCookDelayInSeconds = createSelector(
  selectSlice,
  (slice) => slice.itemCookDelayInSeconds,
);

const selectShouldLoopSound = createSelector(selectSlice, (slice) => slice.loopNotificationSound);

const selectOrdersSortingOrder = createSelector(selectSlice, (slice) => slice.ordersSortingOrder);

const selectCountDownTimerEnable = createSelector(
  selectSlice,
  (slice) => slice.isCountDownTimerEnabled,
);

const selectCountDownTimerBlinkingEnabled = createSelector(
  selectSlice,
  (slice) => slice.isCountDownTimerBlinkingThresholdEnabled,
);

const selectCountDownTimerBlinkingThreshold = createSelector(
  selectSlice,
  (slice) => slice.countDownTimerBlinkingThreshold,
);

const selectItemListInterfaceSettings = createSelector(selectSlice, (slice) => ({
  ...initialSettingsState.interface.itemList,
  ...slice.interface.itemList,
}));

const selectOrderHeaderInterfaceSettings = createSelector(selectSlice, (slice) => ({
  ...initialSettingsState.interface.orderHeader,
  ...slice.interface.orderHeader,
}));
const selectListViewPreviewInterfaceSettings = createSelector(selectSlice, (slice) => ({
  ...initialSettingsState.interface.listViewPreview,
  ...slice.interface.listViewPreview,
}));
const selectActionButtonsInterfaceSettings = createSelector(selectSlice, (slice) => ({
  ...initialSettingsState.interface.actionButtons,
  ...slice.interface.actionButtons,
}));

const selectItemDetailsInterfaceSettings = createSelector(selectSlice, (slice) => ({
  ...initialSettingsState.interface.itemDetails,
  ...slice.interface.itemDetails,
}));

const selectOrderListInterfaceSettings = createSelector(selectSlice, (slice) => ({
  ...initialSettingsState.interface.orderList,
  ...slice.interface.orderList,
}));

const selectOrderDetailsInterfaceSettings = createSelector(selectSlice, (slice) => ({
  ...initialSettingsState.interface.orderDetails,
  ...slice.interface.orderDetails,
}));

const selectSelectedSoundAndAlertConfig = createSelector(
  selectSlice,
  (slice) => slice.selectedSoundAndAlertConfig,
);

const selectCardViewLayoutLayout = createSelector(
  selectSlice,
  (slice) => slice.cardViewLayout.layout,
);
const selectCardViewLayoutCollapsibility = createSelector(
  selectSlice,
  (slice) => slice.cardViewLayout.collapsibility,
);
const selectIsGridViewCollapsed = createSelector(selectSlice, (slice) => slice.isGridViewCollapsed);
const selectCardViewLayoutStatusTab = createSelector(
  selectSlice,
  (slice) => slice.cardViewLayout.statusTab,
);

const selectCardViewLayoutAllTabColumn = createSelector(
  selectSlice,
  (slice) => slice.cardViewLayout.allTabColumn,
);
const selectCardViewLayoutNewTabColumn = createSelector(
  selectSlice,
  (slice) => slice.cardViewLayout.newTabColumn,
);
const selectCardViewLayoutCookingTabColumn = createSelector(
  selectSlice,
  (slice) => slice.cardViewLayout.cookingTabColumn,
);

export default {
  selectAllCategories,
  selectScreenCategories,
  selectUserSelectedCategories,
  selectSelectedCategories,
  selectIsSelectedAllCategories,
  selectSelectedCategoriesCount,

  selectAllOrderKinds,
  selectUserSelectedOrderKinds,
  selectSelectedOrderKinds,
  selectIsSelectedAllOrderKinds,
  selectSelectedOrderKindsCount,

  selectItemCookDelayEnabled,
  selectItemCookDelayInSeconds,
  selectShouldLoopSound,
  selectOrdersSortingOrder,
  selectCountDownTimerEnable,
  selectCountDownTimerBlinkingEnabled,
  selectCountDownTimerBlinkingThreshold,

  selectItemListInterfaceSettings,
  selectItemDetailsInterfaceSettings,
  selectOrderListInterfaceSettings,
  selectOrderDetailsInterfaceSettings,
  selectSelectedSoundAndAlertConfig,
  selectCardViewLayoutLayout,
  selectCardViewLayoutStatusTab,
  selectCardViewLayoutAllTabColumn,
  selectCardViewLayoutNewTabColumn,
  selectCardViewLayoutCookingTabColumn,
  selectCardViewLayoutCollapsibility,
  selectIsGridViewCollapsed,
  selectOrderHeaderInterfaceSettings,
  selectListViewPreviewInterfaceSettings,
  selectActionButtonsInterfaceSettings,
};
